import React, { useContext, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import DataRoomDocumentsContext from '../../providers/DataRoomDocuments/DataRoomDocuments.context';
import DataRoomDocument from '../DataRoomDocument';
import { Checkbox, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Link, Params, useParams } from 'react-router-dom';
import ArrowupIcon from 'icons/Arrowup.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DataRoomDocEmpty from '../DataRoomDocEmpty';
import useQueryParams from 'hooks/useQueryParams';
import useMatchMedia from 'hooks/useMatchMedia';
import SortIcon from 'icons/Sort.icon';
import useFileDownload from 'hooks/useFileDownload';
import { MoveDocument } from '../DataRoomDocumentDrawers/MoveDocument';
import { MoveDocumentRef } from '../DataRoomDocumentDrawers/MoveDocument/MoveDocument.component';
import DataRoomActionMenu from '../DataRoomActionMenu';
import useDeleteFile from '../../hooks/useDeleteFile';
import DataRoomSelectedDocumentsContext from '../../providers/DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import { RenameModal } from '../DataRoomDocumentDrawers/RenameModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import MymeTypeIcon from '../MymeTypeIcon';
import DownloadIcon from 'icons/Download.icon';
import EyeIcon from 'icons/Eye.icon';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import TruckIcon from 'icons/Truck.icon';
import TrashIcon from 'icons/Trash.icon';
import PencilIcon from 'icons/Pencil.icon';
import useSelectAndDeselect from './hooks/useSelectAndDeselect';
import KeyIcon from 'icons/Key.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import ProductsIcon from 'icons/Products.icon';
import DrNodeTagDrawer from '../DrNodeTagDrawer';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import useReplaceFile from '../../hooks/useReplaceFile';
import ArrowClockIcon from 'icons/ArrowClock.icon';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import LockopenIcon from 'icons/Lockopen.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import useFindSuggestedDataRoomNodeWithTag from './hooks/useFindSuggestedDataRoomTag';
import ManageFileAccess from '../DataRoomDocumentDrawers/ManageFileAccess';
import {
  getArrayLength,
  getFileIdsForShareableLinks,
  getNodeMimeType,
} from 'utils';
import useDisplayedItemsWithPlaceholders from './hooks/useDisplayedItemsWithPlaceholder';
import CreateShareableLinkContext from 'router/subrouters/DataRoom/providers/CreateShareableLink/CreateShareableLink.context';
import LinkIcon from 'icons/Link.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import ResolvePopup from 'components/ResolvePopup';
import useUploadingPlaceholders from './hooks/useUploadingPlaceholders';
import { getMimeTypeByNameExtension } from 'uploadFileUtils';

import './DataRoomDocList.styles.scss';
import './DataRoomDocList.styles.responsive.scss';

export type DataRoomDocListProps = {
  className?: string;
};

export const SORTING_VALUES = {
  sortName: 'sortName',
  '-sortName': '-sortName',
  modifiedAt: 'modifiedAt',
  '-modifiedAt': '-modifiedAt',
  numberOfDownloads: 'numberOfDownloads',
  '-numberOfDownloads': '-numberOfDownloads',
  numberOfViews: 'numberOfViews',
  '-numberOfViews': '-numberOfViews',
  viewedDate: 'viewedDate',
  '-viewedDate': '-viewedDate',
};

const DataRoomDocList: React.FC<DataRoomDocListProps> = (props) => {
  const { className } = props;

  const {
    isAdmin,
    isBuySide,
    hasIndexGenerated,
    doesntHaveAccess,
    canUserCreateShareableLinks,
    dataRoom: { _id: dataRoomId },
    setDataRoom,
    openBuyQaDrawer,
    setSelectedForQAndADrawerForDrawer,
  } = useContext(DataRoomContext);
  const {
    currentlyDisplayedNodes,
    areDocsLoading,
    documentRoute,
    baseDocsPath,
    areInsightsShown,
    buySidePreviewMember,
    canTagFiles,
    baseLevelNodes,
    setCurrentlySelectedFolder,
    setCurrentlyDisplayedNodes,
  } = useContext(DataRoomDocumentsContext);
  const {
    selected,
    isAllSelected,
    isSingleSelected,
    hasAuthorizedActionsForAllSelected,
    canShareFiles,
    shouldRename,
    shouldAllowBulkActions,
    isSingleSelectedFolder,
    isSingleSelectedVideo,
    canDownloadSelected,
    isSelectedMode,
    onSelect,
    removeAll,
    selectAll,
    updateSelected,
    updateSelectedValue,
  } = useContext(DataRoomSelectedDocumentsContext);
  const { openCreateShareableLink } = useContext(CreateShareableLinkContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { documentId } = useParams<{ documentId: string }>();

  const { placeholders, displayedNodeIdsUploading } =
    useUploadingPlaceholders(documentId);

  const displayedItemsWithPlaceholders = useDisplayedItemsWithPlaceholders(
    currentlyDisplayedNodes,
    placeholders,
  );

  const onDocDelete = useDeleteFile();

  const dataRoomTagBanner = useFindSuggestedDataRoomNodeWithTag(
    currentlyDisplayedNodes,
  );

  const { onReplaceClick, getInputProps, restOfRootProps } = useReplaceFile(
    isSingleSelected ? selected[0] : null,
  );

  const {
    containersRefs,
    draggableOverlayRef,
    docListRef,
    isDragging,
    isRequestPending,
    setIsDragging,
    setIsRequestPending,
  } = useSelectAndDeselect();

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const onShareableLink = useCallback(() => {
    openCreateShareableLink(getFileIdsForShareableLinks(selected));
  }, [selected, openCreateShareableLink]);

  const { setQueryParam, params } = useQueryParams<Params>();

  const [moveFiles, moveFilesRef] = useCallbackRef<MoveDocumentRef | null>(
    null,
  );
  const [manageFilesAccess, manageFilesAccessRef] =
    useCallbackRef<SideDrawerRef | null>(null);
  const [renameTheFile, renameTheFileRef] = useCallbackRef<ModalRef | null>(
    null,
  );
  const [tagThisFileDrawer, tagThisFileDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);

  const classes = classNames('data-room-doc-list', className);

  const prevNode = useMemo(
    () =>
      documentRoute.length > 1 ? documentRoute[documentRoute.length - 2] : null,
    [documentRoute],
  );

  const onSelectAll = useCallback(() => {
    if (isAllSelected) {
      removeAll();
    } else {
      selectAll();
    }
  }, [isAllSelected, removeAll, selectAll]);

  const isFolderEmpty = useMemo(
    () =>
      !getArrayLength(currentlyDisplayedNodes) &&
      !getArrayLength(placeholders.files) &&
      !getArrayLength(placeholders.folders),
    [currentlyDisplayedNodes, placeholders],
  );

  const showLevelUp = useMemo(
    () => !!documentRoute.length,
    [documentRoute.length],
  );

  const onActionMenuDelete = useCallback(
    () => onDocDelete(selected, setIsRequestPending, true),
    [onDocDelete, selected, setIsRequestPending],
  );

  const onTagSet = useCallback(
    (node: DataRoomNode) => {
      setCurrentlyDisplayedNodes((old) =>
        old.map((el) => (el._id === node._id ? node : el)),
      );
      setDataRoom(({ existTags, ...rest }) => ({
        ...rest,
        existTags: [...existTags.filter((tag) => tag === node.tag), node.tag],
      }));

      if (selected?.length) updateSelectedValue(node);
    },
    [
      selected?.length,
      setCurrentlyDisplayedNodes,
      setDataRoom,
      updateSelectedValue,
    ],
  );

  const removeTagFromOldNode = useCallback(
    (nodeId: string) => {
      setCurrentlyDisplayedNodes((old) =>
        old.map((el) => (el._id === nodeId ? { ...el, tag: null } : el)),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const onChangeSecureDownload = useCallback(async () => {
    if (!isSingleSelected || isSingleSelectedVideo || isSingleSelectedFolder)
      return;

    const node = {
      ...selected[0],
      downloadSecurePdf: !selected[0].downloadSecurePdf,
    };

    try {
      await dataRoomApi.nodes.changeIsSecuredDownload(
        node._id,
        node.downloadSecurePdf,
      );

      setCurrentlyDisplayedNodes((old) =>
        old.map((el) => (el._id === node._id ? { ...el, ...node } : el)),
      );

      updateSelectedValue(node);

      popSuccess(
        t(
          node.downloadSecurePdf
            ? 'enableRemoteTerminationSuccess'
            : 'disableRemoteTerminationSucceess',
          { fileName: node.name },
        ),
      );
    } catch (e) {
      popServerError(e);
    }
  }, [
    isSingleSelected,
    isSingleSelectedFolder,
    isSingleSelectedVideo,
    selected,
    setCurrentlyDisplayedNodes,
    updateSelectedValue,
    t,
    popSuccess,
    popServerError,
  ]);

  const shouldChangeTag = useMemo(
    () => isSingleSelected && canTagFiles && !isSingleSelectedFolder,
    [canTagFiles, isSingleSelected, isSingleSelectedFolder],
  );

  const { download } = useFileDownload(dataRoomId, baseLevelNodes);

  const handleDownload = useCallback(
    (nodes: DataRoomNode[]) => {
      download(nodes);
      removeAll();
    },
    [download, removeAll],
  );

  const dropdownOptionsBulk = useMemo(() => {
    if (!shouldAllowBulkActions || !selected.length) return null;

    return [
      ...(canShareFiles
        ? [
            {
              label: t('manage'),
              onClick: () => manageFilesAccess?.setOpen(true),
              icon: <GroupUsersIcon strokeWidth={2} />,
            },
          ]
        : []),
      ...(canShareFiles && canUserCreateShareableLinks
        ? [
            {
              label: t('shareFiles'),
              onClick: onShareableLink,
              icon: <LinkIcon />,
            },
          ]
        : []),
      ...(hasAuthorizedActionsForAllSelected
        ? [
            {
              label: t('move'),
              onClick: moveFiles?.open,
              icon: <TruckIcon />,
            },
          ]
        : []),
      ...(shouldRename
        ? [
            {
              label: t('rename'),
              onClick: renameTheFile?.open,
              icon: <PencilIcon />,
            },
          ]
        : []),
      ...(canDownloadSelected
        ? [
            {
              label: t('download'),
              onClick: () => handleDownload(selected),
              icon: <DownloadIcon />,
            },
          ]
        : []),
      ...(shouldChangeTag
        ? [
            {
              label: t('tagThisFile'),
              onClick: () => tagThisFileDrawer.setOpen(true),
              icon: <ProductsIcon />,
            },
          ]
        : []),
      ...(isSingleSelected &&
      !isSingleSelectedFolder &&
      !isSingleSelectedVideo &&
      hasAuthorizedActionsForAllSelected
        ? [
            {
              label: t(
                selected[0].downloadSecurePdf
                  ? 'disableRemoteTermination'
                  : 'enableRemoteTermination',
              ),
              onClick: onChangeSecureDownload,
              icon: selected[0].downloadSecurePdf ? (
                <LockopenIcon />
              ) : (
                <LockclosedIcon />
              ),
            },
            {
              label: t('addNewVersion'),
              onClick: onReplaceClick,
              icon: <ArrowClockIcon />,
            },
          ]
        : []),
      ...(hasAuthorizedActionsForAllSelected
        ? [
            {
              label: t('delete'),
              onClick: onActionMenuDelete,
              icon: <TrashIcon />,
              alert: true,
            },
          ]
        : []),
    ];
  }, [
    shouldAllowBulkActions,
    hasAuthorizedActionsForAllSelected,
    selected,
    moveFiles,
    shouldRename,
    renameTheFile,
    isSingleSelected,
    isSingleSelectedFolder,
    isSingleSelectedVideo,
    tagThisFileDrawer,
    shouldChangeTag,
    manageFilesAccess,
    canDownloadSelected,
    canUserCreateShareableLinks,
    canShareFiles,
    onActionMenuDelete,
    t,
    onReplaceClick,
    onChangeSecureDownload,
    onShareableLink,
    handleDownload,
  ]);

  const updateNode = useCallback(
    (node: DataRoomNode) => {
      setCurrentlyDisplayedNodes((prev) =>
        prev.map((n) => (n._id === node._id ? node : n)),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const updateNodes = useCallback(
    (dataRoomNodes: DataRoomNode[]) => {
      setCurrentlyDisplayedNodes((prev) =>
        prev.map((el) => {
          const foundNode = dataRoomNodes.find((node) => el._id === node._id);
          return foundNode
            ? {
                ...el,
                ...foundNode,
              }
            : el;
        }),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const DraggableOverlayCard = useMemo(() => {
    if (!isDragging || !selected.length || !hasAuthorizedActionsForAllSelected)
      return null;

    const numOfSelected = selected.length;

    const displayNode = selected?.[numOfSelected - 1];

    return (
      <div ref={draggableOverlayRef} className="draggable-overlay">
        <MymeTypeIcon mimeType={getNodeMimeType(displayNode)} />
        <p>{displayNode.name}</p>
        <Counter
          count={numOfSelected}
          isPositionAbsolute
          variant={COUNTER_VARIANTS.PRIMARY}
        />
      </div>
    );
  }, [
    draggableOverlayRef,
    hasAuthorizedActionsForAllSelected,
    isDragging,
    selected,
  ]);

  const generateSortableSpan = useCallback(
    (keyword: keyof typeof SORTING_VALUES, copy: React.ReactNode) => {
      const selected = [
        SORTING_VALUES[keyword],
        SORTING_VALUES[`-${keyword}` as keyof typeof SORTING_VALUES],
      ].includes(params.sort);

      const transcending = SORTING_VALUES[keyword] === params.sort;
      return (
        <span
          className={classNames('keep-on-phone', 'sorting-button', keyword, {
            selected,
            transcending,
          })}
          onClick={() => {
            setQueryParam(
              'sort',
              params.sort === SORTING_VALUES[keyword]
                ? SORTING_VALUES[`-${keyword}` as keyof typeof SORTING_VALUES]
                : SORTING_VALUES[keyword],
            );
          }}
        >
          {copy}
          <div className="sorting-arrows">
            {selected ? (
              <ArrowdownIcon className="arrow" />
            ) : (
              <>
                <SortIcon className="chevron first" />
                <SortIcon className="chevron" />
              </>
            )}
          </div>
        </span>
      );
    },

    [params, setQueryParam],
  );

  const renderNode = useCallback(
    (node: DataRoomNode, index: number, isSelected: boolean, suggestedTag) => {
      const isUploading = displayedNodeIdsUploading.some(
        (_id) => _id === node._id,
      );

      return (
        <div
          key={node._id}
          className={classNames('document-wrapper', {
            'uploading-placeholder': isUploading,
          })}
          ref={(ref) => (containersRefs.current[index] = ref)}
          {...restOfRootProps}
        >
          <input {...getInputProps()} />
          <DataRoomDocument
            node={node}
            isUploading={isUploading}
            updateNode={updateNode}
            suggestedTag={suggestedTag}
            dropdownOptions={dropdownOptionsBulk}
            isRequestPending={isRequestPending}
            setIsRequestPending={setIsRequestPending}
            onDeleteDocument={setCurrentlyDisplayedNodes}
            onSelect={isPhablet ? updateSelected : onSelect}
            isSelectedMode={isSelectedMode}
            isSelected={isSelected}
            setIsDragging={(val) =>
              hasAuthorizedActionsForAllSelected && setIsDragging(val)
            }
            onQandA={() => {
              openBuyQaDrawer();
              setSelectedForQAndADrawerForDrawer(node);
            }}
            areInsightsShown={areInsightsShown}
            onDownload={(node: DataRoomNode) => handleDownload([node])}
            onTagSet={onTagSet}
            removeTagFromOldNode={removeTagFromOldNode}
            canEnterEditMode={isSelected && selected.length === 1}
            canTagFiles={canTagFiles}
            onViewFileModalOpen={removeAll}
          />
        </div>
      );
    },
    [
      areInsightsShown,
      canTagFiles,
      containersRefs,
      displayedNodeIdsUploading,
      dropdownOptionsBulk,
      getInputProps,
      handleDownload,
      hasAuthorizedActionsForAllSelected,
      isPhablet,
      isRequestPending,
      isSelectedMode,
      onSelect,
      onTagSet,
      openBuyQaDrawer,
      removeAll,
      removeTagFromOldNode,
      restOfRootProps,
      selected.length,
      setCurrentlyDisplayedNodes,
      setIsDragging,
      setIsRequestPending,
      setSelectedForQAndADrawerForDrawer,
      updateNode,
      updateSelected,
    ],
  );

  const renderPlaceholder = useCallback(
    ({ _id, isPlaceholderFolder, type, name }, index: number) => (
      <div key={_id + index} className="document-wrapper uploading-placeholder">
        <div className="data-room-document uploading">
          <div className="data-room-document__content">
            <div className="data-room-document__left">
              <MymeTypeIcon
                mimeType={
                  isPlaceholderFolder
                    ? undefined
                    : type || getMimeTypeByNameExtension(name)
                }
              />
              <div className="data-room-document__info">
                <p className="data-room-document__name">{name}</p>
              </div>
            </div>
            <div className="data-room-document__right">
              <span className="uploading-text">{t('uploading')}...</span>
            </div>
          </div>
        </div>
      </div>
    ),
    [t],
  );

  return (
    <div className={classes} ref={docListRef}>
      {!isFolderEmpty && (
        <div className="data-room-doc-list__heading">
          <div>
            {isPhablet && isSelectedMode && (
              <Checkbox
                onChange={onSelectAll}
                checked={!!selected.length}
                className={classNames({ mixed: !isAllSelected })}
              />
            )}

            {hasIndexGenerated && (
              <span className="index">
                <KeyIcon />
              </span>
            )}
            {generateSortableSpan('sortName', t('aToZ'))}
          </div>
          <div>
            {isAdmin &&
              !buySidePreviewMember &&
              areInsightsShown &&
              generateSortableSpan(
                'numberOfDownloads',
                <DownloadIcon className="copy-icon" />,
              )}
            {isAdmin &&
              areInsightsShown &&
              generateSortableSpan(
                'numberOfViews',
                <EyeIcon className="copy-icon" />,
              )}

            {isBuySide && generateSortableSpan('viewedDate', t('viewed'))}

            {(isAdmin || isBuySide) && <span>{t('access')}</span>}

            <ResolvePopup
              onHover
              className="direct-children"
              popupContent={t('folderItemCountsFiles')}
            >
              {t('files')} (#)
            </ResolvePopup>

            <span>{t('size')}</span>
            {generateSortableSpan('modifiedAt', t('modified'))}
          </div>
        </div>
      )}

      {DraggableOverlayCard}

      {areDocsLoading ? (
        <Loader />
      ) : (
        <>
          {displayedItemsWithPlaceholders.folders.map((item: any, index) => {
            if (item.isPlaceholder) {
              return renderPlaceholder(item, index);
            }

            const node = item;
            const isSelected = selected.some(({ _id }) => _id === node._id);
            const suggestedTag =
              dataRoomTagBanner?._id === node._id
                ? dataRoomTagBanner?.tag
                : null;

            return renderNode(node, index, isSelected, suggestedTag);
          })}

          {displayedItemsWithPlaceholders.files.map((item: any, index) => {
            if (item.isPlaceholder) {
              return renderPlaceholder(
                item,
                index + displayedItemsWithPlaceholders.folders.length,
              );
            }

            const node = item as DataRoomNode;
            const isSelected = selected.some(({ _id }) => _id === node._id);
            const suggestedTag =
              dataRoomTagBanner?._id === node._id
                ? dataRoomTagBanner?.tag
                : null;

            return renderNode(
              node,
              index + displayedItemsWithPlaceholders.folders.length,
              isSelected,
              suggestedTag,
            );
          })}
        </>
      )}

      {!doesntHaveAccess && isSelectedMode && (
        <DataRoomActionMenu
          onClose={removeAll}
          onDelete={onActionMenuDelete}
          onRename={renameTheFile?.open}
          onMove={moveFiles?.open}
          onManage={() => manageFilesAccess?.setOpen(true)}
          onDownload={() => handleDownload(selected)}
          onShareFiles={onShareableLink}
        />
      )}

      <MoveDocument nodes={selected} ref={moveFilesRef} />

      <ManageFileAccess
        ref={manageFilesAccessRef}
        nodes={selected}
        parentId={documentId}
        updateNodes={updateNodes}
      />

      {isSingleSelected && (
        <RenameModal node={selected[0]} ref={renameTheFileRef} />
      )}

      {shouldChangeTag && (
        <DrNodeTagDrawer
          ref={tagThisFileDrawerRef}
          onSuccessTag={onTagSet}
          node={selected[0]}
          removeTagFromOldNode={removeTagFromOldNode}
        />
      )}

      {isFolderEmpty && !areDocsLoading && <DataRoomDocEmpty />}

      {showLevelUp && !areDocsLoading && (
        <Link
          className="link-back"
          onClick={() => setCurrentlySelectedFolder(prevNode)}
          to={`${baseDocsPath}${prevNode ? prevNode._id : ''}`}
        >
          <ArrowupIcon />
          {t('backUpALevel')}
        </Link>
      )}
    </div>
  );
};

export default DataRoomDocList;
